import { truncateName } from "@/api/helper";
import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import Pagination from "@/components/core/pagination";
import { Searchbar } from "@/components/core/searchbar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { caseRecordsQueryOptions } from "@/features/case-record/api/queries/case-records-query";
import { rafflesQueryOptions } from "@/features/case-record/api/queries/raffle-query";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import type { Raffle } from "@/features/case-record/types/raffle";
import { formatDate } from "@/utils/format-date";
import { IconPlus } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

export const Route = createFileRoute("/_backoffice/raffle")({
	validateSearch: z.object({
		status: z.string().optional(),
		search: z.string().optional(),
		per_page: z.number().optional(),
		page: z.number().nullable().optional(),
	}),
	loaderDeps: ({ search }) => ({
		page: search.page,
		per_page: search.per_page,
		status: search.status || "for_raffling",
		tabs: search.status || "for_raffling",
		search: search.search,
	}),
	loader: ({ context: { queryClient }, deps }) => {
		return queryClient.ensureQueryData(
			caseRecordsQueryOptions({
				...deps,
			}),
		);
	},
	component: RouteComponent,
});

const defferedColumns: ColumnDef<CaseRecord>[] = [
	{
		id: "case_title",
		label: "Case Title",
		cell: ({ data, onClick }) => (
			<Link to={`/raffle/create/${data.id}?status=${data.status}`} className="font-semibold text-blue-600 hover:text-blue-500">
				{data.case_title}
			</Link>
		),
	},
	{
		id: "deferred_remarks",
		label: "Remarks",
		cell: ({ data }) => <span>{data.defer_remarks}</span>,
	},
	{
		id: "deferred_at",
		label: "Date Deferred",
		cellClassName: "text-right",
		headerClassName: "text-right",
		cell: ({ data }) => data.deferred_at,
	},
];

const caseColumns: ColumnDef<Raffle>[] = [
	{
		id: "case_number",
		label: "Case Number",
		cell: ({ data, onClick }) => (
			<Link to={`/raffle/create/${data.id}`} className="font-semibold text-blue-600 hover:text-blue-500">
				{data.case_number}
			</Link>
		),
	},
	{
		id: "case_title",
		label: "Case Title",
		cell: ({ data }) => <span>{truncateName(data.case_title, 35)}</span>,
	},
	{
		id: "filed_at",
		label: "Date Filed",
		cellClassName: "text-right",
		headerClassName: "text-right",
		cell: ({ data }) => data.filed_at,
	},
];

const jdrColumns: ColumnDef<Raffle>[] = [
	{
		id: "case_number",
		label: "Case Number",
		cell: ({ data, onClick }) => (
			<Link to={`/raffle/create/${data.id}`} className="font-semibold text-blue-600 hover:text-blue-500">
				{data.case_number}
			</Link>
		),
	},
	{
		id: "case_title",
		label: "Case Title",
		cell: ({ data }) => <span>{truncateName(data.case_title, 35)}</span>,
	},
	{
		id: "branch",
		label: "Handling Court",
		cell: ({ data }) => <span>{data.branch}</span>,
	},
	{
		id: "filed_at",
		label: "Date Filed",
		cellClassName: "text-right",
		headerClassName: "text-right",
		cell: ({ data }) => data.filed_at,
	},
];

const jdrRaffledColumns: ColumnDef<Raffle>[] = [
	{
		id: "case_number",
		label: "Case Number",
		cell: ({ data, onClick }) => (
			<Link to={`/raffle/create/${data.id}`} className="font-semibold text-blue-600 hover:text-blue-500">
				{data.case_number}
			</Link>
		),
	},
	{
		id: "case_title",
		label: "Case Title",
		cell: ({ data }) => <span>{truncateName(data.case_title, 35)}</span>,
	},
	{
		id: "branch",
		label: "Handling Court",
		cell: ({ data }) => <span>{data.branch}</span>,
	},
	{
		id: "jdrBranch",
		label: "JDR Branch",
		cell: ({ data }) => <span>{data.jdrBranch}</span>,
	},
	{
		id: "filed_at",
		label: "Date Filed",
		cellClassName: "text-right",
		headerClassName: "text-right",
		cell: ({ data }) => data.filed_at,
	},
];

function RouteComponent() {
	const search = Route.useSearch();
	const caseQuery = useSuspenseQuery(
		caseRecordsQueryOptions({
			...search,
			status: search.status || "for_raffling",
		}),
	);

	const cases = caseQuery.data.data;
	const meta = caseQuery.data.meta;
	const navigate = useNavigate();

	return (
		<Content pageName="Raffle">
			{/*<Header title="Raffle" />*/}
			<div className="flex flex-col w-full max-w-base">
				<div className="flex flex-col w-full p-6">
					<div className="flex flex-col w-full gap-4">
						<Searchbar />
						<Tabs
							value={search.tabs || "new_cases"}
							defaultValue={search.tabs || "new_cases"}
							onValueChange={(value) => {
								navigate({
									to: "/raffle",
									search: (old) => ({
										...old,
										tabs: value,
									}),
								});
							}}
						>
							<TabsList className="justify-center">
								<TabsTrigger value="new_cases" count={meta.count_per_status?.for_raffling}>
									New Cases
								</TabsTrigger>
								<TabsTrigger value="for_jdr">JDR</TabsTrigger>
							</TabsList>

							<TabsContent value="new_cases">
								<Tabs
									defaultValue={search.status || "for_raffling"}
									onValueChange={async (value) => {
										await navigate({
											to: "/raffle",
											search: (old) => ({
												...old,
												status: value,
											}),
										});
									}}
								>
									<TabsList>
										<TabsTrigger value="for_raffling" count={meta.count_per_status?.for_raffling}>
											For Raffling
										</TabsTrigger>
										<TabsTrigger value="raffled">Raffled</TabsTrigger>
										<TabsTrigger value="deferred_raffling" count={meta.count_per_status?.deferred_raffling}>
											Deferred
										</TabsTrigger>
									</TabsList>

									<TabsContent value="raffled">
										<div className="flex flex-col w-full gap-4 mt-4">
											<DataTable
												className="border-collapse"
												data={cases}
												columns={caseColumns}
												stickyHead={false}
												stickyCol={false}
												maxHeight={480}
												showBorder={false}
												compact={false}
											/>

											<Pagination
												onClick={async (page) => {
													await navigate({
														to: "/raffle",
														search: (old) => ({
															...old,
															page,
														}),
													});
												}}
												initialPage={meta.current_page}
												pageCount={meta.last_page}
											/>
										</div>
									</TabsContent>

									<TabsContent value="for_raffling">
										<div className="flex flex-col w-full gap-4 mt-4">
											<DataTable
												className="border-collapse"
												data={cases}
												columns={caseColumns}
												stickyHead={false}
												stickyCol={false}
												maxHeight={480}
												showBorder={false}
												compact={false}
											/>

											<Pagination
												onClick={async (page) => {
													await navigate({
														to: "/raffle",
														search: (old) => ({
															...old,
															page,
														}),
													});
												}}
												initialPage={meta.current_page}
												pageCount={meta.last_page}
											/>
										</div>
									</TabsContent>

									<TabsContent value="deferred_raffling">
										<div className="flex flex-col w-full gap-4 mt-4">
											<DataTable
												className="border-collapse"
												data={cases}
												columns={defferedColumns}
												stickyHead={false}
												stickyCol={false}
												maxHeight={480}
												showBorder={false}
												compact={false}
											/>

											<Pagination
												onClick={async (page) => {
													await navigate({
														to: "/raffle",
														search: (old) => ({
															...old,
															page,
														}),
													});
												}}
												initialPage={meta.current_page}
												pageCount={meta.last_page}
											/>
										</div>
									</TabsContent>
								</Tabs>
							</TabsContent>
							<TabsContent value="for_jdr">
								<Tabs
									defaultValue={search.status || "for_raffling"}
									onValueChange={async (value) => {
										await navigate({
											to: "/raffle",
											search: (old) => ({
												...old,
												status: value,
											}),
										});
									}}
								>
									<TabsList>
										<TabsTrigger value="for_raffling">For Raffling</TabsTrigger>
										<TabsTrigger value="raffled">Raffled</TabsTrigger>
									</TabsList>

									<TabsContent value="for_raffling">
										<div className="flex flex-col w-full gap-4 mt-4">
											<DataTable
												className="border-collapse"
												data={cases}
												columns={jdrColumns}
												stickyHead={false}
												stickyCol={false}
												maxHeight={480}
												showBorder={false}
												compact={false}
											/>

											<Pagination
												onClick={async (page) => {
													await navigate({
														to: "/raffle",
														search: (old) => ({
															...old,
															page,
														}),
													});
												}}
												initialPage={meta.current_page}
												pageCount={meta.last_page}
											/>
										</div>
									</TabsContent>

									<TabsContent value="raffled">
										<div className="flex flex-col w-full gap-4 mt-4">
											<DataTable
												className="border-collapse"
												data={cases}
												columns={jdrRaffledColumns}
												stickyHead={false}
												stickyCol={false}
												maxHeight={480}
												showBorder={false}
												compact={false}
											/>

											<Pagination
												onClick={async (page) => {
													await navigate({
														to: "/raffle",
														search: (old) => ({
															...old,
															page,
														}),
													});
												}}
												initialPage={meta.current_page}
												pageCount={meta.last_page}
											/>
										</div>
									</TabsContent>
								</Tabs>
							</TabsContent>
						</Tabs>
					</div>
				</div>
			</div>
		</Content>
	);
}
